import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import { isCartUpsertConcurrencyError } from '@/lib/cartErrors'

import type { CadyMarket } from '@/types/CadyMarket'

import type { CartStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

/**
 * Timing instrument for the cart-bundle upsert round trip (SUN-292, Animation A0a).
 *
 * **Why this exists.** The reward-unlock sequence trails one `upsertCartBundle`
 * response — nothing animates until the re-priced cart comes back — so the wait
 * is part of the experience the business is being asked to sign off on. The
 * debug harness (A0b) simulates that wait, and its latency presets have to come
 * from observed numbers: a guessed-optimistic preset makes the sandbox feel
 * better than production and sets expectations the live implementation cannot
 * meet. This is where the numbers come from.
 *
 * **Reading it.** Each call emits one New Relic `PageAction`:
 *
 * ```
 * SELECT percentile(durationMs, 50, 95) FROM PageAction
 * WHERE actionName = 'cartUpsertTiming' AND outcome = 'success' AND retryCount = 0
 * FACET mode SINCE 1 week ago
 * ```
 *
 * Filter to `outcome = 'success'` for the wait the sequence actually trails, and
 * to `retryCount = 0` for the clean round trip — then drop both filters to see
 * the tail the retry preset has to reproduce.
 *
 * **Two properties of the environment that the attributes exist to work around.**
 * Local dev and the demo build both point at the same New Relic application
 * (`.env.demo` overrides no `VITE_NEW_RELIC_*` var), so `mode` is what separates
 * `demo` rows from a developer's laptop. And this is deliberately *not* gated on
 * `VITE_REPORT_ERRORS_TO_NEW_RELIC` — that flag is `false` outside production to
 * keep dev noise out of the app's error rate, which alerting is built on,
 * whereas a `PageAction` is query-only data with nothing hanging off it. Gating
 * timings on it would mean no numbers from demo, which is the one place they
 * were asked for.
 *
 * Scope is the upsert alone, per the plan. The remove-to-re-lock path trails a
 * DELETE round trip and is not measured here.
 */

/**
 * How the upsert round trip ended.
 *
 * `ConcurrencyNoop` is separate because it is the outcome that changes nothing:
 * the store returns without assigning `cartData`, so the subtotal never moves
 * and no sequence runs at all (see `isCartUpsertConcurrencyError`). Leaving those
 * rows in a latency percentile would flatter it — they resolve fast and buy the
 * customer nothing.
 */
export enum CartUpsertOutcome {
  Success = 'success',
  ConcurrencyNoop = 'concurrencyNoop',
  Failure = 'failure'
}

/** New Relic `PageAction` name — the `actionName` to filter on in NRQL. */
export const CART_UPSERT_TIMING_ACTION = 'cartUpsertTiming'

interface CartUpsertContext {
  /**
   * Market the upsert was made in. The likeliest explanation for variance
   * between two otherwise identical calls — catalog size and the number of
   * promotions the server evaluates both differ by market.
   */
  cadyMarket: CadyMarket | null
  /**
   * True when the upsert creates a cart bundle rather than updating one. An add
   * and an update are different server-side work, and only the add path can
   * grant a reward.
   */
  isNewCartBundle: boolean
}

/**
 * Times `request`, reports the result, and returns or rethrows exactly what the
 * request did. Wrapping the call rather than bracketing it with start/stop calls
 * is what makes it impossible for a code path to be timed and never reported.
 */
export async function measureCartUpsert(
  context: CartUpsertContext,
  request: () => Promise<AxiosResponse<CartStorefrontDto>>
): Promise<AxiosResponse<CartStorefrontDto>> {
  const startedAt = performance.now()

  try {
    const response = await request()

    reportTiming(CartUpsertOutcome.Success, startedAt, context, {
      statusCode: response.status,
      retryCount: retryCountOf(response.config),
      responseBytes: wireBytesOf(response),
      ...cartShapeOf(response.data)
    })

    return response
  } catch (error) {
    // A cart-dependency rejection lands in `Failure` alongside a genuine 5xx.
    // Both are a wait that ends in no cart change; the `statusCode` attribute is
    // there to tell them apart without a fourth outcome.
    const outcome = isCartUpsertConcurrencyError(error)
      ? CartUpsertOutcome.ConcurrencyNoop
      : CartUpsertOutcome.Failure

    reportTiming(outcome, startedAt, context, {
      statusCode: axios.isAxiosError(error) ? error.response?.status : undefined,
      retryCount: axios.isAxiosError(error) ? retryCountOf(error.config) : undefined
    })

    throw error
  }
}

function reportTiming(
  outcome: CartUpsertOutcome,
  startedAt: number,
  context: CartUpsertContext,
  outcomeAttributes: Record<string, unknown>
): void {
  const attributes: Record<string, unknown> = {
    durationMs: Math.round(performance.now() - startedAt),
    outcome,
    mode: import.meta.env.MODE,
    cadyMarket: context.cadyMarket,
    isNewCartBundle: context.isNewCartBundle,
    ...outcomeAttributes
  }

  // Send only the attributes we actually have. An absent attribute is
  // unambiguous in NRQL (`WHERE responseBytes IS NULL`); an explicitly
  // `undefined` one depends on how the agent serializes it.
  Object.keys(attributes).forEach((key) => {
    if (attributes[key] === undefined) delete attributes[key]
  })

  // Local only, not demo: on demo the numbers are read off NRQL, and a console
  // line per add there is noise in a customer-reachable build.
  if (import.meta.env.DEV) {
    console.debug(`[${CART_UPSERT_TIMING_ACTION}]`, attributes)
  }

  window.newrelic?.addPageAction(CART_UPSERT_TIMING_ACTION, attributes)
}

function cartShapeOf(cart: CartStorefrontDto): Record<string, number> {
  const newCartBundleIds = cart.newCartBundleIds ?? []
  const cartBundles = cart.cartBundles ?? []

  return {
    cartBundleCount: cartBundles.length,
    newBundleCount: newCartBundleIds.length,
    // Rewards this upsert granted. Worth its own attribute because the crossing
    // is both the slowest case — the server re-prices and grants — and the one
    // the animation exists for, so its latency must be separable from an
    // ordinary add's rather than averaged into it.
    grantedRewardCount: cartBundles.filter(
      (cartBundle) =>
        newCartBundleIds.includes(cartBundle.cartBundleId) && !!cartBundle.sourcePromotionId
    ).length
  }
}

/**
 * Wire size of the response, from `Content-Length`. Compressed size when the
 * response is gzipped, which is the figure that governs transfer time, and
 * absent entirely when the server answers chunked — hence `undefined` rather
 * than a zero that would read as an empty payload.
 */
function wireBytesOf(response: AxiosResponse): number | undefined {
  const bytes = Number(response.headers?.['content-length'])
  return Number.isFinite(bytes) ? bytes : undefined
}

/**
 * How many times `axios-retry` re-sent this request. Read off the request
 * config, which it stamps in place, so a success *after* retries still reports
 * the count. It matters because the two configured retries add ~600–720ms of
 * `exponentialDelay` backoff on top of three round trips: a retried call belongs
 * in its own bucket, not in the p95 of clean ones, and it is the worst case
 * A0b's retry preset has to reproduce.
 */
function retryCountOf(config: InternalAxiosRequestConfig | undefined): number {
  return config?.['axios-retry']?.retryCount ?? 0
}
